import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";

const SchoolERPPage = ({ city }) => {

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-in-out", once: true });
  }, []);

  const features = [
    { title: "Admin Dashboard", desc: `Manage students, staff, fees, reports, and operations in ${city}.` },
    { title: "Teacher Panel", desc: "Track attendance, upload assignments, share study material, and publish results." },
    { title: "Student Panel", desc: "Access homework, test results, assignments, and academic progress." },
    { title: "Mobile App", desc: "Receive instant updates, notifications, and monitor performance on mobile." },
    { title: "Course Material", desc: "Upload and manage syllabus, notes, PDFs, videos, and resources easily." },
    { title: "Online Exams", desc: "Conduct exams online, auto-generate results, and analyze performance." },
    { title: "Assignment Management", desc: "Assign homework digitally and track student submissions." },
    { title: "Attendance Automation", desc: "Real-time tracking with daily, weekly, and monthly reports." },
    { title: "Fee & Finance", desc: "Automate fee collection, online payments, and financial reporting." }
  ];

  const steps = ["Requirement Analysis", "System Setup", "Training & Onboarding", "Go Live Support"];

  return (
    <div className="bg-gray-50">

      {/* META */}
      <Helmet>
        <title>Best School ERP Software in {city} | Gurukul ERP</title>
        <meta
          name="description"
          content={`Looking for School ERP Software in ${city}? Gurukul ERP provides modern school management solutions for schools, colleges, and coaching institutes in ${city}.`}
        />
        <meta
          name="keywords"
          content={`School ERP Software ${city}, School Management System ${city}, Best School ERP ${city}`}
        />
        <link rel="canonical" href={`https://www.gurukulerp.in/${city.toLowerCase()}`} />
      </Helmet>

      {/* HERO */}
      <section className="relative py-24 bg-gradient-to-r from-[#4a148c] to-[#6a1b9a] text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-col-reverse md:flex-row items-center gap-12">
          <div className="md:w-1/2 text-center md:text-left" data-aos="fade-right">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
              Transform Your School with Gurukul ERP in {city}
            </h1>
            <p className="mb-4 text-gray-200">
              Modern, cloud-based School ERP to automate academics, finance, attendance, and communication for schools in {city}.
            </p>
            <p className="mb-6 text-gray-200">
              Simplify administration, improve transparency, and enhance student learning experiences with our all-in-one platform.
            </p>
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <Link
                to="/contact"
                className="bg-yellow-500 hover:bg-yellow-600 px-6 py-3 rounded-full font-semibold transition transform hover:scale-105"
              >
                Request Demo
              </Link>
              <Link
                to="/"
                className="border border-white px-6 py-3 rounded-full hover:bg-white hover:text-[#4a148c] transition transform hover:scale-105"
              >
                See Features
              </Link>
            </div>
          </div>

          <div className="md:w-1/2 flex justify-center" data-aos="fade-left">
            <img
              src="/herosectionhr.png"
              alt={`School ERP Software ${city}`}
              className="w-full max-w-[500px] rounded-xl shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-20" data-aos="fade-up">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#4a148c] mb-6">
            Complete Digital School Management in {city}
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            Gurukul ERP helps {city} schools digitize operations from admissions to examinations, fee management, and HR. Experience faster workflows, accurate data, and seamless communication in one integrated platform.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#4a148c] mb-4" data-aos="fade-up">
            Key Features of {city} School ERP
          </h2>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16" data-aos="fade-up">
            Everything your school needs in a single platform — academics, finance, attendance, communication, and more.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group bg-[#f3e5f5] p-8 rounded-2xl shadow-md hover:shadow-xl transition transform hover:scale-105 relative overflow-hidden"
                data-aos="zoom-in"
                data-aos-delay={idx * 100}
              >
                <div className="absolute -top-10 -right-10 w-20 h-20 bg-yellow-100 rounded-full group-hover:scale-125 transition"></div>
                <div className="w-14 h-14 flex items-center justify-center bg-[#4a148c] text-white rounded-full mb-5 text-xl font-bold shadow-md relative z-10">
                  {idx + 1}
                </div>
                <h3 className="text-lg font-semibold text-[#4a148c] mb-3 relative z-10">{feature.title}</h3>
                <p className="text-gray-700 text-sm relative z-10">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 bg-gradient-to-r from-[#e1bee7] to-[#f3e5f5]">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div data-aos="fade-right">
            <img
              src="/ChatGPT Image Feb 28, 2026, 05_03_03 PM.png"
              alt={`School ERP Software ${city}`}
              className="rounded-xl shadow-lg"
            />
          </div>
          <div data-aos="fade-left">
            <h2 className="text-3xl font-semibold text-[#4a148c] mb-6">
              Benefits for Schools in {city}
            </h2>
            <ul className="space-y-4 text-gray-700 text-lg">
              <li>✔ Reduce administrative workload and errors</li>
              <li>✔ Improve data accuracy and transparency</li>
              <li>✔ Secure cloud-based storage</li>
              <li>✔ Real-time student performance tracking</li>
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-semibold text-[#4a148c] mb-12" data-aos="fade-up">
            Implementation Process in {city}
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="bg-[#f3e5f5] p-6 rounded-xl shadow-md"
                data-aos="flip-left"
                data-aos-delay={idx * 150}
              >
                <div className="text-2xl font-bold text-yellow-500 mb-3">0{idx+1}</div>
                <h3 className="font-semibold text-[#4a148c] mb-2">{step}</h3>
                <p className="text-gray-700 text-sm">
                  Smooth ERP implementation for schools in {city}.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#4a148c] text-white text-center" data-aos="zoom-in">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-4">
            Ready to Digitize Your School in {city}?
          </h2>
          <p className="mb-6 text-lg">
            Upgrade your institution with Gurukul ERP, a secure and scalable school management solution.
          </p>
          <Link
            to="/contact"
            className="bg-yellow-500 hover:bg-yellow-600 transition px-8 py-3 rounded-full font-medium transform hover:scale-105"
          >
            Get Free Consultation
          </Link>
        </div>
      </section>

    </div>
  );
};

export default SchoolERPPage;